import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Copy, Gift, Share2, Trophy } from "lucide-react";
import { track } from "../lib/analytics";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/referrals/me");
        setData(r);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!data) return null;

  const link = `${window.location.origin}/register?ref=${data.code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      track.referralCopy(data.code);
      toast.success("Referral link copied!");
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  const share = async () => {
    const text = `I'm using Feel Greats for personalized health guidance. Get 10% off your first plan with my code: ${data.code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Feel Greats", text, url: link });
        track.referralShare(data.code);
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  };

  return (
    <Card className="rounded-2xl border-amber-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50" data-testid="referral-card">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
              <Gift className="w-3.5 h-3.5" /> Refer & earn
            </div>
            <h3 className="font-display text-2xl text-emerald-950 mt-1">
              Give 10% off, get a free bonus product.
            </h3>
            <p className="text-sm text-emerald-900/70 mt-1 max-w-md">
              Share your code. When a friend buys their first plan, they save 10% — and you earn
              a bonus product credit on your next order.
            </p>
          </div>
          <div className="flex items-center gap-3 bg-white border border-emerald-100 rounded-xl px-4 py-3">
            <Trophy className="w-5 h-5 text-amber-500" />
            <div>
              <div className="text-xs text-emerald-900/60">Successful referrals</div>
              <div className="font-display text-2xl text-emerald-950" data-testid="ref-count">
                {data.successful_referrals}
              </div>
            </div>
            <div className="ml-3 pl-3 border-l border-emerald-100">
              <div className="text-xs text-emerald-900/60">Bonus credits</div>
              <div className="font-display text-2xl text-emerald-950" data-testid="bonus-count">
                {data.bonus_credits}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <Input
            readOnly
            value={link}
            className="rounded-xl bg-white border-emerald-200 font-mono text-sm"
            data-testid="referral-link-input"
          />
          <Button
            onClick={copy}
            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="copy-referral-btn"
          >
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
          <Button
            onClick={share}
            variant="outline"
            className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50"
            data-testid="share-referral-btn"
          >
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
        </div>

        <div className="mt-3 text-xs text-emerald-900/60">
          Your code: <span className="font-semibold text-emerald-800" data-testid="ref-code">{data.code}</span>
        </div>
      </CardContent>
    </Card>
  );
}
